import { useCallback, useEffect, useRef, type Dispatch, type SetStateAction } from 'react'
import { crmApi } from '@/api'
import { isError } from '@/helpers/checkQuery'
import debounce from '@/utils/debounce/debounce'
import websocket from '@/utils/websocket'
import {
  CRM_REALTIME_LEAD_UPDATED_DEBOUNCE_MS,
  CRM_REALTIME_LIST_REFETCH_DEBOUNCE_MS,
  isIntegrationMessageDeletedWsMessage,
  isIntegrationMessageInsertedWsMessage,
  isIntegrationMessageReadWsMessage,
  isLeadUpdatedWsMessage,
  isVkMessageInsertedWsMessage,
  isVkMessageReadWsMessage,
  normalizeVkUnreadCount,
  useRefetchOnBrowserTabVisible,
} from '../../../utils/crmRealtimeWebSocket'
import { withLeadVkChatFromPayload } from '../../../utils/leadVkChatMeta'
import {
  patchLeadProviderChatMeta,
  patchLeadProviderChatUnreadZero,
} from '../../../utils/leadFields'
import { parseCrmChatProviderKey } from '../../integrations/common/lib/crmChatProviderKey'
import { tryParseLeadFromGetLeadResponse } from '../leadModalRefetchMerge'
import type { LeadProfileLead } from '../LeadProfileModal.types'
import type { LeadRefetchInModalOptions } from './leadRefetch.types'

/** Тихий refetch меты чатов + WS unread / lead_updated при открытой карточке лида. */
export function useLeadRefetchRealtime(params: {
  open: boolean
  leadId?: number
  setLead: Dispatch<SetStateAction<LeadProfileLead>>
  /** Полный silent refetch карточки (поля/статус после automation). */
  refetchLeadInModal?: (
    funnelId?: number | null,
    options?: LeadRefetchInModalOptions,
  ) => Promise<LeadProfileLead | undefined>
  resolvedFunnelId?: number | null
}) {
  const { open, leadId, setLead, refetchLeadInModal, resolvedFunnelId } = params

  const refetchLeadChatMetaSilent = useCallback(async () => {
    const numericLeadId = Number(leadId)
    if (!Number.isFinite(numericLeadId) || numericLeadId <= 0) return

    try {
      const response = await crmApi.getLead({ lead_id: numericLeadId })
      if (isError(response)) return

      // Как в leadModalRefetchMerge: integrations могут быть на data или data.lead
      const parsedLead = tryParseLeadFromGetLeadResponse(response)
      const payload = response?.data?.data
      if (parsedLead == null && payload == null) return

      setLead((prev) => {
        if (!prev) return prev
        if (payload != null) {
          return withLeadVkChatFromPayload(prev, payload) as LeadProfileLead
        }
        return withLeadVkChatFromPayload(prev, parsedLead) as LeadProfileLead
      })
    } catch {
      // тихий catch-up счётчиков интеграций — без алертов
    }
  }, [leadId, setLead])

  const refetchLeadChatMetaSilentRef = useRef(refetchLeadChatMetaSilent)
  refetchLeadChatMetaSilentRef.current = refetchLeadChatMetaSilent

  const debouncedVisibleTabVkRefetchRef = useRef(
    debounce(() => {
      void refetchLeadChatMetaSilentRef.current()
    }, CRM_REALTIME_LIST_REFETCH_DEBOUNCE_MS),
  )

  /** При возврате на вкладку — integrations.* / vk unread, без перезаписи fields. */
  useRefetchOnBrowserTabVisible(() => {
    debouncedVisibleTabVkRefetchRef.current()
  }, open && leadId != null && Number.isFinite(Number(leadId)) && Number(leadId) > 0)

  const debouncedVkWsLeadRefetchRef = useRef(
    debounce(() => {
      void refetchLeadChatMetaSilentRef.current()
    }, CRM_REALTIME_LIST_REFETCH_DEBOUNCE_MS),
  )

  const refetchLeadInModalRef = useRef(refetchLeadInModal)
  refetchLeadInModalRef.current = refetchLeadInModal
  const resolvedFunnelIdRef = useRef(resolvedFunnelId)
  resolvedFunnelIdRef.current = resolvedFunnelId

  const debouncedLeadUpdatedRefetchRef = useRef(
    debounce(() => {
      void refetchLeadInModalRef.current?.(resolvedFunnelIdRef.current, {
        silent: true,
      })
    }, CRM_REALTIME_LEAD_UPDATED_DEBOUNCE_MS),
  )

  /** WS: сразу патчим unread из payload + тихий get-lead для полной меты. */
  useEffect(() => {
    if (
      !open ||
      leadId == null ||
      !Number.isFinite(Number(leadId)) ||
      Number(leadId) <= 0
    ) {
      return
    }

    const numericLeadId = Number(leadId)

    websocket.connect()

    const applyUnreadFromWs = (
      providerRaw: string | null | undefined,
      unreadRaw: unknown,
    ) => {
      const provider = parseCrmChatProviderKey(providerRaw) ?? 'vkontakte'
      const unreadCount = normalizeVkUnreadCount(unreadRaw)

      setLead((prev) => {
        if (!prev) {
          return prev
        }

        if (unreadCount <= 0) {
          return patchLeadProviderChatUnreadZero(
            prev,
            provider,
          ) as LeadProfileLead
        }

        return patchLeadProviderChatMeta(prev, provider, {
          unread_count: unreadCount,
        }) as LeadProfileLead
      })
    }

    const onVkWs = (message: unknown) => {
      if (isIntegrationMessageInsertedWsMessage(message)) {
        const payload = message.data.payload
        if (Number(payload.lead_id) !== numericLeadId) {
          return
        }
        applyUnreadFromWs(payload.provider, payload.unread_count)
        debouncedVkWsLeadRefetchRef.current()
        return
      }

      // unread_count в read = 0 только для conversation, не сумма по лиду/провайдеру.
      if (isIntegrationMessageReadWsMessage(message)) {
        if (Number(message.data.payload.lead_id) !== numericLeadId) {
          return
        }
        debouncedVkWsLeadRefetchRef.current()
        return
      }

      if (isIntegrationMessageDeletedWsMessage(message)) {
        const payload = message.data.payload
        if (Number(payload.lead_id) !== numericLeadId) {
          return
        }
        applyUnreadFromWs(payload.provider, payload.unread_count)
        debouncedVkWsLeadRefetchRef.current()
        return
      }

      if (
        !isVkMessageInsertedWsMessage(message) &&
        !isVkMessageReadWsMessage(message)
      ) {
        return
      }

      if (Number(message.data.payload.lead_id) !== numericLeadId) {
        return
      }

      applyUnreadFromWs('vkontakte', message.data.payload.unread_count)
      debouncedVkWsLeadRefetchRef.current()
    }

    const onLeadUpdatedWs = (message: unknown) => {
      if (!isLeadUpdatedWsMessage(message)) return
      if (Number(message.data.payload.lead_id) !== numericLeadId) return
      // field_id может быть null (set_status / менеджер / задача) — всё равно refetch.
      debouncedLeadUpdatedRefetchRef.current()
    }

    const unsubscribeInserted = websocket.subscribe(
      'vk_message_inserted',
      onVkWs,
    )
    const unsubscribeRead = websocket.subscribe('vk_message_read', onVkWs)
    const unsubscribeIntegration = websocket.subscribe(
      'integration_message_inserted',
      onVkWs,
    )
    const unsubscribeIntegrationRead = websocket.subscribe(
      'integration_message_read',
      onVkWs,
    )
    const unsubscribeIntegrationDeleted = websocket.subscribe(
      'integration_message_deleted',
      onVkWs,
    )
    const unsubscribeLeadUpdated = websocket.subscribe(
      'lead_updated',
      onLeadUpdatedWs,
    )

    return () => {
      unsubscribeInserted?.()
      unsubscribeRead?.()
      unsubscribeIntegration?.()
      unsubscribeIntegrationRead?.()
      unsubscribeIntegrationDeleted?.()
      unsubscribeLeadUpdated?.()
    }
  }, [leadId, open, setLead])
}
